import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { clearAmenityUsageReport } from "../../../Redux/Reports/ReportsReducer";
import { getAmenityUsageReport } from "../../../Redux/Reports/ReportsActions";
import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toastHelper from "../../../Util/toastHelper";
import makeAnimated from "react-select/animated";
import { Switch } from "react-animated-switch";
import html2pdf from "html2pdf.js";
import "./AmenityUsageReport.css";
import Select from "react-select";
import moment from "moment";

const animatedComponents = makeAnimated();

const optionItems = [
  { label: "Current Month", value: "currentMonth" },
  { label: "Last two months", value: "2months" },
  { label: "Last three months", value: "3months" },
  { label: "Last six months", value: "6months" },
];

function AmenityUsageReport() {
  const pdfRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [selectedBuilding, setSelectedBuilding] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [customDateRange, setCustomDateRange] = useState<boolean>(false);
  const { buildings, buildingsLoading } = useSelector(
    (state: RootState) => state.buildings,
  );
  const { amenityUsageReportData, amenityUsageReportLoading } = useSelector(
    (state: RootState) => state.reports,
  );

  const optionsToDates = (
    monthOption: string,
  ): { startDate: string; endDate: string } => {
    if (monthOption === "currentMonth") {
      return {
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().endOf("month").format("YYYY-MM-DD"),
      };
    }

    if (monthOption === "2months") {
      return {
        startDate: moment()
          .subtract(2, "months")
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    }

    if (monthOption === "3months") {
      return {
        startDate: moment()
          .subtract(3, "months")
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    }

    if (monthOption === "6months") {
      return {
        startDate: moment()
          .subtract(6, "months")
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD"),
    };
  };

  useEffect(() => {
    dispatch(getAllBuildings());

    return () => {
      dispatch(clearAmenityUsageReport());
    };
  }, []);

  const getReportHandler = () => {
    if (!selectedBuilding?.length) {
      toastHelper.error("Please select a building");
      return;
    }
    if (customDateRange && (!startDate || !endDate)) {
      toastHelper.error("Please select a start and end date");
      return;
    }
    if (!customDateRange && !selectedMonth) {
      toastHelper.error("Please select a month");
      return;
    }

    if (customDateRange) {
      const today = moment().startOf("day");
      if (moment(endDate).isAfter(today)) {
        toastHelper.error("End date cannot be in the future");
        return;
      }
      if (moment(startDate).isAfter(moment(endDate))) {
        toastHelper.error("Start date cannot be after end date");
        return;
      }
      if (moment(endDate).isBefore(moment(startDate))) {
        toastHelper.error("End date cannot be before start date");
        return;
      }
    }

    if (amenityUsageReportLoading) {
      return;
    }

    const buildingIds = selectedBuilding?.map((building: any) =>
      Number(building.value),
    );

    if (selectedMonth) {
      dispatch(
        getAmenityUsageReport({
          buildingIds,
          startDate: optionsToDates(selectedMonth).startDate,
          endDate: optionsToDates(selectedMonth).endDate,
        }),
      );
    }
    if (customDateRange) {
      dispatch(
        getAmenityUsageReport({
          buildingIds,
          startDate: startDate,
          endDate: endDate,
        }),
      );
    }
  };

  const todayDate = moment().format("YYYY-MM-DD");

  const options: any = buildings?.map((building: any) => ({
    label: building.buildingName,
    value: building.buildingId.toString(),
    data: building,
  }));

  const amenitiesChartData =
    amenityUsageReportData?.amenities
      ?.map((amenity: any) => ({
        name: amenity?.amenityName,
        label: `${amenity?.amenityName} (${amenity?.buildingName})`,
        usageCount: Number(amenity?.usageCount) || 0,
        buildingName: amenity?.buildingName,
        amenityTypeName: amenity?.amenityTypeName,
        memberEntryCount: Number(amenity?.memberEntryCount) || 0,
        guestEntryCount: Number(amenity?.guestEntryCount) || 0,
      }))
      ?.slice()
      ?.sort(
        (a: { usageCount: number }, b: { usageCount: number }) =>
          b.usageCount - a.usageCount,
      ) ?? [];

  const chartHeight = Math.max(amenitiesChartData.length * 36, 300);

  const downloadPdfHandler = async () => {
    if (!pdfRef.current) return;

    const element = pdfRef.current as HTMLElement;

    const originalWidth = element.style.width;
    const originalMaxWidth = element.style.maxWidth;

    element.style.width = "1500px";
    element.style.maxWidth = "1500px";
    element.style.padding = "20px";

    await new Promise((resolve) => setTimeout(resolve, 500));

    const width = element.scrollWidth;
    const height = element.scrollHeight;

    const pdfOptions: any = {
      margin: 0,
      filename: `AmenityUsageReport-${moment().format("DD-MM-YYYY")}.pdf`,
      image: {
        type: "jpeg",
        quality: 1,
      },
      html2canvas: {
        scale: 3,
        useCORS: true,
        windowWidth: 2000,
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: {
        unit: "px",
        format: [width, height],
        orientation: "portrait",
        compress: true,
      },
      pagebreak: {
        mode: [],
      },
    };

    try {
      await html2pdf().set(pdfOptions).from(element).save();
    } finally {
      element.style.width = originalWidth;
      element.style.maxWidth = originalMaxWidth;
    }
  };

  return (
    <React.Fragment>
      {(buildingsLoading || amenityUsageReportLoading) && <Loading />}
      <div className="aur-main-container">
        <div className="aur-heading-container">
          <h1 className="aur-heading">Amenity Usage Report</h1>
        </div>
        <div className="aur-top-container">
          <div className="aur-input-container">
            <Select
              classNamePrefix="aur-custom-select"
              closeMenuOnSelect={false}
              components={animatedComponents}
              value={selectedBuilding}
              isOptionDisabled={() => selectedBuilding.length === 3}
              isMulti
              options={options ?? []}
              onChange={(e: any) => {
                if (e?.length <= 3) {
                  setSelectedBuilding(e);
                } else {
                  toastHelper.error("You can select up to 3 buildings");
                }
              }}
            />
          </div>
          {!customDateRange && (
            <div className="aur-input-container">
              <select
                className="aur-input"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {!selectedMonth && <option value="">Select a month</option>}
                {optionItems.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {customDateRange && (
            <React.Fragment>
              <div className="aur-input-container">
                <input
                  type="date"
                  value={startDate}
                  max={endDate || todayDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (endDate && moment(value).isAfter(moment(endDate))) {
                      toastHelper.error("Start date cannot be after end date");
                      return;
                    }
                    setStartDate(value);
                  }}
                  className="aur-input"
                />
              </div>
              <div className="aur-input-container">
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  max={todayDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (moment(value).isAfter(moment(todayDate))) {
                      toastHelper.error("End date cannot be in the future");
                      return;
                    }
                    if (startDate && moment(value).isBefore(moment(startDate))) {
                      toastHelper.error("End date cannot be before start date");
                      return;
                    }
                    setEndDate(value);
                  }}
                  className="aur-input"
                />
              </div>
            </React.Fragment>
          )}

          <div className="aur-switch-container">
            <p className="aur-text">Custom Date Range</p>
            <Switch
              checked={customDateRange}
              onChange={(val) => {
                setStartDate("");
                setEndDate("");
                setSelectedMonth("");
                setCustomDateRange(val);
              }}
              activeTrackColor="#000000"
            />
          </div>

          <div className="aur-button-container">
            <button className="aur-button" onClick={getReportHandler}>
              Get Report
            </button>
          </div>
          {amenityUsageReportData && (
            <div className="aur-button-container">
              <button className="aur-button" onClick={downloadPdfHandler}>
                Download PDF
              </button>
            </div>
          )}
        </div>
        {amenityUsageReportData && (
          <div className="aur-data-container" ref={pdfRef}>
            <div className="aur-main-heading-container">
              <h1 className="aur-heading">Amenity Usage</h1>
              <div className="aur-building-name-container">
                {selectedBuilding?.map((item: any, i: number) => (
                  <h1 key={i} className="aur-building-name">
                    {item?.label}
                  </h1>
                ))}
              </div>
            </div>

            <div className="aur-heading-container">
              <div className="aur-design-container"></div>
              <div className="aur-heading-content">
                <h2 className="aur-heading">Amenities</h2>
                <p className="aur-sub-heading">
                  Usage count for each amenity during the selected period.
                </p>
              </div>
            </div>

            {amenitiesChartData.length > 0 ? (
              <div className="aur-horizontal-chart-container">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={amenitiesChartData}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      hide
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={220}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(0,0,0,0.03)" }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const item = payload[0]?.payload;
                        return (
                          <div className="aur-chart-tooltip">
                            <p className="aur-tooltip-title">{item?.name}</p>
                            <p className="aur-tooltip-text">
                              Building: {item?.buildingName}
                            </p>
                            <p className="aur-tooltip-text">
                              Type: {item?.amenityTypeName}
                            </p>
                            <p className="aur-tooltip-text">
                              Usage: {item?.usageCount}
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="usageCount"
                      fill="#F4B7B8"
                      radius={[0, 4, 4, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="aur-no-data-container">
                <p className="aur-no-data-text">No amenities available</p>
              </div>
            )}

            <div className="aur-design-line"></div>
          </div>
        )}
        {!amenityUsageReportData && (
          <div className="aur-no-data-container">
            <p className="aur-no-data-text">No data available</p>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

export default AmenityUsageReport;
